import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MOBILE_BREAKPOINT } from '../../../utils/responsive';

export type DialogMode = 'create' | 'edit' | 'view';

interface UseResponsiveDialogOptions {
	mode: DialogMode;
	disabled?: boolean;
}

interface UseResponsiveDialogReturn {
	theme: ReturnType<typeof useTheme>;
	isMobile: boolean;
	isReadOnly: boolean;
	isViewMode: boolean;
	isEditMode: boolean;
	isCreateMode: boolean;
}

/**
 * Hook for handling responsive dialog behavior and read-only state.
 * Extracts common responsive logic from dialog components.
 *
 * @param options.mode - Dialog mode: 'create', 'edit', or 'view'
 * @param options.disabled - Optional flag to force read-only state
 * @returns Responsive state and computed flags
 *
 * @example
 * const { isMobile, isReadOnly } = useResponsiveDialog({ mode, disabled });
 */
export function useResponsiveDialog({
	mode,
	disabled = false,
}: UseResponsiveDialogOptions): UseResponsiveDialogReturn {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));

	const isViewMode = mode === 'view';
	const isEditMode = mode === 'edit';
	const isCreateMode = mode === 'create';
	const isReadOnly = isViewMode || disabled;

	return {
		theme,
		isMobile,
		isReadOnly,
		isViewMode,
		isEditMode,
		isCreateMode,
	};
}

export default useResponsiveDialog;
